package uz.mint.frontend.components.sections

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import uz.mint.frontend.PageGutter
import uz.mint.frontend.ShellStyle
import uz.mint.frontend.components.widgets.*
import uz.mint.frontend.api.SessionState
import uz.mint.frontend.api.rememberSession
import uz.mint.frontend.i18n.Locale
import uz.mint.frontend.i18n.SiteStrings
import uz.mint.frontend.i18n.accountStringsFor
import uz.mint.frontend.toSitePalette
import uz.mint.frontend.alignItems
import uz.mint.frontend.alignSelf
import uz.mint.frontend.flexDirectionCss
import uz.mint.frontend.flexWrapCss
import uz.mint.frontend.gridTemplateColumnsCss
import uz.mint.frontend.justifyContent
import uz.mint.frontend.marginInlineAuto
import uz.mint.frontend.placeItems
import uz.mint.frontend.widthCss
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.ui.thenIf

/**
 * Sticky site header.
 *
 * @param anchorBase prefix for the in-page anchor links. Empty on the landing page, where the
 *   sections are on the page; the catalogue page passes its locale's home path so `#tariffs`
 *   becomes `/#tariffs` and the nav still goes somewhere real.
 * @param localePath where each entry in the language menu leads. Defaults to that locale's home
 *   page; the catalogue page overrides it so switching language keeps you on the catalogue.
 */
@Composable
fun NavHeader(
    locale: Locale,
    strings: SiteStrings,
    anchorBase: String = "",
    localePath: (Locale) -> String = { it.homePath },
) {
    val palette = ColorMode.current.toSitePalette()

    Div(
        Modifier
            .fillMaxWidth()
            .position(Position.Sticky)
            .top(0.px)
            .zIndex(40)
            .backgroundColor(palette.paper)
            .borderBottom(2.px, LineStyle.Solid, palette.rule)
            .toAttrs()
    ) {
        Row(
            ShellStyle.toModifier()
                .padding(topBottom = 12.px, leftRight = PageGutter)
                .minHeight(78.px)
                .gap(20.px)
                .flexWrapCss(),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Link(
                path = "$anchorBase#$TOP_ANCHOR",
                modifier = Modifier.display(org.jetbrains.compose.web.css.DisplayStyle.Flex),
            ) {
                Img(src = palette.lockup, alt = "mint.uz", attrs = Modifier.height(26.px).toAttrs())
            }

            Row(
                Modifier.gap(28.px).flexWrapCss().margin(left = 16.px)
                    .fontSize(15.px).fontWeight(FontWeight.SemiBold),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                NavLink("$anchorBase#$TARIFFS_ANCHOR", strings.navPlans)
                NavLink("$anchorBase#$ADVANTAGES_ANCHOR", strings.navHow)
                NavLink("#$FOOTER_ANCHOR", strings.navSupport)
            }

            Spacer()

            AccountLink(locale)
            LanguageMenu(locale, localePath, palette.rule, palette.card, palette.tint, palette.ink)
            ThemeToggle()

            Link(
                path = "$anchorBase#$TARIFFS_ANCHOR",
                modifier = Modifier
                    .backgroundColor(palette.mint)
                    .color(palette.onMint)
                    .fontWeight(FontWeight.Bold)
                    .fontSize(15.px)
                    .padding(topBottom = 13.px, leftRight = 22.px)
                    .letterSpacing((-0.01).em)
                    .whiteSpace(WhiteSpace.NoWrap),
                variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
            ) { SpanText(strings.navCta) }
        }
    }
}

/**
 * Sign-in state in the header.
 *
 * Renders nothing while the session is still being resolved. The header is in the statically
 * exported HTML, so the first paint always happens before the answer arrives — showing "Войти"
 * during that gap would flash a sign-in prompt at customers who are already signed in, on every
 * single page load.
 */
@Composable
private fun AccountLink(locale: Locale) {
    val palette = ColorMode.current.toSitePalette()
    val account = accountStringsFor(locale)

    val (path, label) = when (rememberSession()) {
        SessionState.Loading -> return
        SessionState.Anonymous -> locale.loginPath to account.navLogin
        is SessionState.SignedIn -> locale.profilePath to account.navAccount
    }

    Link(
        path = path,
        modifier = Modifier
            .border(2.px, LineStyle.Solid, palette.rule)
            .padding(topBottom = 9.px, leftRight = 14.px)
            .fontSize(13.px)
            .fontWeight(FontWeight.Bold)
            .letterSpacing(0.02.em)
            .whiteSpace(WhiteSpace.NoWrap),
        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
    ) { SpanText(label) }
}

@Composable
private fun NavLink(path: String, text: String) {
    Link(
        path = path,
        text = text,
        modifier = Modifier.whiteSpace(WhiteSpace.NoWrap),
        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
    )
}

/**
 * Language picker.
 *
 * Every entry is a real link to that locale's route (`/`, `/uz/`, `/en/`) rather than an
 * in-place text swap like the design's prototype does. The three locales are separately
 * exported pages so crawlers can index each language; swapping text client-side would collapse
 * them onto one URL and throw away exactly the SEO the ТЗ asked for.
 */
@Composable
private fun LanguageMenu(
    locale: Locale,
    localePath: (Locale) -> String,
    rule: com.varabyte.kobweb.compose.ui.graphics.Color,
    card: com.varabyte.kobweb.compose.ui.graphics.Color,
    tint: com.varabyte.kobweb.compose.ui.graphics.Color,
    ink: com.varabyte.kobweb.compose.ui.graphics.Color,
) {
    var open by remember { mutableStateOf(false) }

    Div(Modifier.position(Position.Relative).toAttrs()) {
        Button(
            attrs = Modifier
                .display(org.jetbrains.compose.web.css.DisplayStyle.Flex)
                .alignItems("center")
                .gap(9.px)
                .border(2.px, LineStyle.Solid, rule)
                .backgroundColor(card)
                .color(ink)
                .padding(topBottom = 8.px, leftRight = 11.px)
                .fontSize(13.px)
                .fontWeight(FontWeight.Bold)
                .letterSpacing(0.04.em)
                .cursor(com.varabyte.kobweb.compose.css.Cursor.Pointer)
                .onClick { open = !open }
                .toAttrs { attr("aria-haspopup", "listbox") }
        ) {
            LocaleFlag(locale)
            SpanText(locale.label)
            ChevronDownIcon(color = ink)
        }

        if (open) {
            Div(
                Modifier
                    .position(Position.Absolute)
                    .top(100.percent)
                    .right(0.px)
                    .zIndex(60)
                    .minWidth(196.px)
                    .border(2.px, LineStyle.Solid, rule)
                    .backgroundColor(card)
                    .display(org.jetbrains.compose.web.css.DisplayStyle.Flex)
                    .flexDirectionCss("column")
                    .toAttrs { attr("role", "listbox") }
            ) {
                Locale.entries.forEachIndexed { index, entry ->
                    Link(
                        path = localePath(entry),
                        modifier = Modifier
                            .fillMaxWidth()
                            .display(org.jetbrains.compose.web.css.DisplayStyle.Flex)
                            .alignItems("center")
                            .gap(11.px)
                            .padding(topBottom = 11.px, leftRight = 14.px)
                            .fontSize(14.px)
                            .fontWeight(FontWeight.SemiBold)
                            .textAlign(TextAlign.Start)
                            .thenIf(index < Locale.entries.lastIndex) {
                                Modifier.borderBottom(2.px, LineStyle.Solid, rule)
                            },
                        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    ) {
                        LocaleFlag(entry)
                        SpanText(entry.nativeName, Modifier.flex(1))
                        SpanText(
                            entry.label,
                            Modifier.fontSize(11.px).fontWeight(FontWeight.Bold)
                                .letterSpacing(0.1.em).opacity(0.5),
                        )
                    }
                }
            }
        }
    }
}


@Composable
private fun LocaleFlag(locale: Locale) = when (locale) {
    Locale.RU -> FlagRu()
    Locale.UZ -> FlagUz()
    Locale.EN -> FlagEn()
}

@Composable
private fun ThemeToggle() {
    var colorMode by ColorMode.currentState
    val palette = colorMode.toSitePalette()

    Button(
        attrs = Modifier
            .size(42.px)
            .border(2.px, LineStyle.Solid, palette.rule)
            .backgroundColor(palette.card)
            .display(org.jetbrains.compose.web.css.DisplayStyle.Grid)
            .justifyContent("center")
            .alignItems("center")
            .flexShrink(0)
            .cursor(com.varabyte.kobweb.compose.css.Cursor.Pointer)
            .overflow(Overflow.Hidden)
            .onClick { colorMode = colorMode.opposite }
            .toAttrs { attr("aria-label", "Theme") }
    ) {
        if (colorMode.isLight) MoonIcon(color = palette.ink) else SunIcon(color = palette.ink)
    }
}
